import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import ABAApi from '../../../data/api/abaApi';
import { IPerson } from '../../../types/persons/person';

import { AppointmentStatus } from '../../../types/appointments/AppointmentStatus';
import { getShiftSessionUrl } from '../../common/Links';

interface Appointment {
  _id: string;
  person: string;
  type?: string;
  start: string;
  finish: string;
  payEventId?: string;
  status?: AppointmentStatus;
  payment?: {
    total?: number;
  };
}

interface PersonSummary {
  personId: string;
  person: IPerson;
  appointments: Appointment[];
  totalAmount: number;
  gameCount: number;
  supervisorCount: number;
  hasBankDetails: boolean;
  statusCounts: Map<number, number>;
}

interface AppointmentSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  appointments: Appointment[];
  persons: IPerson[];
  payEventName: string;
  onPayEventNameChange: (name: string) => void;
  payEventDescription: string;
  onPayEventDescriptionChange: (description: string) => void;
  onPayEventCreated: (payEvent?: any) => void;
  associationId: string;
  sessionName: string;
  sessionId: string;
  shiftId: string;
  currentPaymentStatus: 'unpaid' | 'partial' | 'paid';
  onPaymentStatusUpdate: (status: 'unpaid' | 'partial' | 'paid') => void;
  error?: string | null;
}

export const AppointmentSelectionModal: React.FC<AppointmentSelectionModalProps> = ({
  opened,
  onClose,
  appointments,
  persons,
  payEventName,
  onPayEventNameChange,
  payEventDescription,
  onPayEventDescriptionChange,
  onPayEventCreated,
  associationId,
  sessionName,
  sessionId,
  shiftId,
  currentPaymentStatus,
  onPaymentStatusUpdate,
  error
}) => {
  // Internal state for this modal
  const [selectedAppointments, setSelectedAppointments] = useState<Set<string>>(new Set());
  const [showSummaryView, setShowSummaryView] = useState(true);
  const [hidePaidAppointments, setHidePaidAppointments] = useState(true);
  const [bankDetailsFilter, setBankDetailsFilter] = useState<string>('all');
  const [isCreatingPayEvent, setIsCreatingPayEvent] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Create person lookup map for performance
  const personMap = useMemo(() => {
    const map = new Map<string, IPerson>();
    persons.forEach(person => {
      map.set(person._id, person);
    });
    return map;
  }, [persons]);

  // Helper function to get status name from enum value
  const getStatusName = (status: number) => {
    return AppointmentStatus.toString(status) || 'Unknown';
  };

  // Filter appointments based on paid status and bank details
  const filteredAppointments = useMemo(() => {
    let filtered = appointments;

    // First filter by paid status if enabled
    if (hidePaidAppointments) {
      filtered = filtered.filter(appointment => !appointment.payEventId);
    }

    // Then filter by bank details
    if (bankDetailsFilter && bankDetailsFilter !== 'all') {
      filtered = filtered.filter(appointment => {
        const person = personMap.get(appointment.person);
        if (!person) return false;
        const hasBankDetails = !!(person.hasBankDetails);

        if (bankDetailsFilter === 'with-bank-details') {
          return hasBankDetails;
        } else if (bankDetailsFilter === 'without-bank-details') {
          return !hasBankDetails;
        }

        return true;
      });
    }

    return filtered;
  }, [appointments, personMap, bankDetailsFilter, hidePaidAppointments]);

  // Count appointments by paid status and bank details
  const appointmentStats = useMemo(() => {
    let withBankDetails = 0;
    let withoutBankDetails = 0;
    let paidAppointments = 0;
    let unpaidAppointments = 0;

    appointments.forEach(appointment => {
      // Count paid status
      if (appointment.payEventId) {
        paidAppointments++;
      } else {
        unpaidAppointments++;
      }

      // Count bank details (for all appointments, not just filtered ones)
      const person = personMap.get(appointment.person);
      if (person) {
        const hasBankDetails = !!(person.hasBankDetails);
        if (hasBankDetails) {
          withBankDetails++;
        } else {
          withoutBankDetails++;
        }
      }
    });

    return {
      withBankDetails,
      withoutBankDetails,
      paidAppointments,
      unpaidAppointments,
      total: appointments.length
    };
  }, [appointments, personMap]);

  // Create person summaries for summary view
  const personSummaries = useMemo(() => {
    const summaries = new Map<string, PersonSummary>();

    filteredAppointments.forEach(appointment => {
      const person = personMap.get(appointment.person);
      if (!person) return;

      const personId = appointment.person;
      if (!summaries.has(personId)) {
        summaries.set(personId, {
          personId,
          person,
          appointments: [],
          totalAmount: 0,
          gameCount: 0,
          supervisorCount: 0,
          hasBankDetails: !!(person.hasBankDetails),
          statusCounts: new Map(),
        });
      }

      const summary = summaries.get(personId)!;
      summary.appointments.push(appointment);
      summary.totalAmount += appointment.payment?.total || 0;

      // Count appointment types
      const appointmentType = appointment.type?.toLowerCase() || '';
      if (appointmentType.includes('game') || appointmentType.includes('referee')) {
        summary.gameCount++;
      } else if (appointmentType.includes('supervisor')) {
        summary.supervisorCount++;
      }

      // Count appointment statuses
      const status = appointment.status ?? -1; // Use -1 for unknown status
      summary.statusCounts.set(status, (summary.statusCounts.get(status) || 0) + 1);
    });

    return Array.from(summaries.values());
  }, [filteredAppointments, personMap]);

  // Simple selection functions
  const toggleAppointment = (appointmentId: string) => {
    setSelectedAppointments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  const selectAllVisible = () => {
    setSelectedAppointments(prev => {
      const newSet = new Set(prev);
      filteredAppointments.forEach(app => {
        if (!app.payEventId) { // Only select unpaid appointments
          newSet.add(app._id);
        }
      });
      return newSet;
    });
  };

  const deselectAllVisible = () => {
    setSelectedAppointments(prev => {
      const newSet = new Set(prev);
      filteredAppointments.forEach(app => {
        newSet.delete(app._id);
      });
      return newSet;
    });
  };

  const selectNone = () => {
    setSelectedAppointments(new Set());
  };

  const togglePersonSelection = (personSummary: PersonSummary) => {
    const allPersonAppointmentsSelected = personSummary.appointments.every(app =>
      selectedAppointments.has(app._id)
    );

    setSelectedAppointments(prev => {
      const newSet = new Set(prev);

      if (allPersonAppointmentsSelected) {
        // If all appointments are selected, deselect them all
        personSummary.appointments.forEach(app => {
          newSet.delete(app._id);
        });
      } else {
        // If not all appointments are selected, select them all (except paid ones)
        personSummary.appointments.forEach(app => {
          if (!app.payEventId) { // Only select unpaid appointments
            newSet.add(app._id);
          }
        });
      }

      return newSet;
    });
  };

  // Calculate derived values
  const selectedTotal = useMemo(() => {
    return filteredAppointments
      .filter(app => selectedAppointments.has(app._id))
      .reduce((sum, app) => sum + (app.payment?.total || 0), 0);
  }, [filteredAppointments, selectedAppointments]);

  const allFilteredSelected = filteredAppointments.length > 0 &&
    filteredAppointments.filter(app => !app.payEventId).every(app => selectedAppointments.has(app._id));

  const isPersonSelected = (personSummary: PersonSummary) => {
    return personSummary.appointments.every(app => selectedAppointments.has(app._id));
  };

  const isPersonIndeterminate = (personSummary: PersonSummary) => {
    const selectedCount = personSummary.appointments.filter(app =>
      selectedAppointments.has(app._id)
    ).length;
    return selectedCount > 0 && selectedCount < personSummary.appointments.length;
  };

  const handleCreatePayEvent = async () => {
    if (selectedAppointments.size === 0) return;

    // Filter out any already paid appointments from selection
    const unpaidAppointmentIds = Array.from(selectedAppointments).filter(appointmentId => {
      const appointment = appointments.find(a => a._id === appointmentId);
      return appointment && !appointment.payEventId;
    });

    if (unpaidAppointmentIds.length === 0) {
      setCreateError('No unpaid appointments selected');
      return;
    }

    setIsCreatingPayEvent(true);
    setCreateError(null);

    try {
      const payEvent = await ABAApi.createAppointmentPayEvent({
        name: payEventName,
        description: payEventDescription,
        associationId: associationId,
        appointmentIds: unpaidAppointmentIds,
        options: { requireBankDetails: true }
      });

      onPayEventCreated(payEvent);
      onClose();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create pay event');
    } finally {
      setIsCreatingPayEvent(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group justify="space-between" style={{ width: '100%' }}>
          <Title order={3}>Create Pay Event - {sessionName}</Title>
          <Button
            size="xs"
            variant="subtle"
            component="a"
            href={getShiftSessionUrl(shiftId, sessionId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Session
          </Button>
        </Group>
      }
      size="xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">

        {/* Manual Payment Status Control */}
        <Stack gap="sm">
          <Alert color="blue">
            This view shows ALL appointments in the shift session, regardless of confirmation status.
          </Alert>
          <Text fw={500}>Session Payment Status</Text>
          <Group gap="xs">
            <Badge>{currentPaymentStatus}</Badge>
            {/* <Select
              value={currentPaymentStatus}
              onChange={(value) => onPaymentStatusUpdate(value as 'unpaid' | 'partial' | 'paid')}
              data={[
                { value: 'unpaid', label: 'Unpaid' },
                { value: 'partial', label: 'Partially Paid' },
                { value: 'paid', label: 'Paid' }
              ]}
              disabled={isCreatingPayEvent}
              style={{ flex: 1 }}
            /> */}
            <Button
              size="xs"
              variant="outline"
              color="orange"
              onClick={() => onPaymentStatusUpdate('partial')}
              disabled={isCreatingPayEvent || currentPaymentStatus === 'partial'}
            >
              Mark Partial
            </Button>
            <Button
              size="xs"
              variant="outline"
              color="green"
              onClick={() => onPaymentStatusUpdate('paid')}
              disabled={isCreatingPayEvent || currentPaymentStatus === 'paid'}
            >
              Mark Paid
            </Button>
          </Group>
        </Stack>

        {/* Payment Status Filter */}
        <Stack gap="sm">
          <Text fw={500}>Filter Appointments</Text>
          <Group gap="md">
            <Checkbox
              checked={hidePaidAppointments}
              onChange={(e) => setHidePaidAppointments(e.target.checked)}
              label={`Hide already paid appointments (${appointmentStats.paidAppointments} paid, ${appointmentStats.unpaidAppointments} unpaid)`}
            />
          </Group>
        </Stack>

        {/* Bank Details Filter */}
        <Stack gap="sm">
          <Text fw={500}>Filter by Bank Details</Text>
          <Button.Group>
            <Button
              size="xs"
              variant={bankDetailsFilter === 'all' ? 'filled' : 'outline'}
              onClick={() => setBankDetailsFilter('all')}
              color="blue"
            >
              All ({appointments.length})
            </Button>
            <Button
              size="xs"
              variant={bankDetailsFilter === 'with-bank-details' ? 'filled' : 'outline'}
              color={bankDetailsFilter === 'with-bank-details' ? 'green' : 'gray'}
              onClick={() => setBankDetailsFilter('with-bank-details')}
            >
              With Bank Details ({appointmentStats.withBankDetails})
            </Button>
            <Button
              size="xs"
              variant={bankDetailsFilter === 'without-bank-details' ? 'filled' : 'outline'}
              color={bankDetailsFilter === 'without-bank-details' ? 'red' : 'gray'}
              onClick={() => setBankDetailsFilter('without-bank-details')}
            >
              Missing Bank Details ({appointmentStats.withoutBankDetails})
            </Button>
          </Button.Group>
        </Stack>

        {/* View Toggle and Selection Controls */}
        <Group justify="space-between">
          <Group>
            <Button size="xs" variant="outline" color="blue" onClick={allFilteredSelected ? deselectAllVisible : selectAllVisible}>
              {allFilteredSelected ? (
                filteredAppointments.length === appointments.length
                  ? `Deselect All (${appointments.length})`
                  : `Deselect Visible (${filteredAppointments.length}/${appointments.length})`
              ) : (
                filteredAppointments.length === appointments.length
                  ? `Select All (${appointments.length})`
                  : `Select Visible (${filteredAppointments.length}/${appointments.length})`
              )}
            </Button>
            <Button size="xs" variant="outline" color="red" onClick={selectNone}>
              Select None
            </Button>
          </Group>
          <Stack gap={0}>
            <Text>
              <strong>{selectedAppointments.size}</strong> appointments selected
            </Text>
            <Text fw={500}>
              Total: <strong>${selectedTotal.toFixed(2)}</strong>
            </Text>
          </Stack>
          <Checkbox
            checked={showSummaryView}
            onChange={(e) => setShowSummaryView(e.target.checked)}
            label="Summary View"
          />
        </Group>

        {/* Appointments Table */}
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 40 }}></Table.Th>
              <Table.Th>Person</Table.Th>
              {showSummaryView ? (
                <>
                  <Table.Th>Total Amount</Table.Th>
                  <Table.Th>Games & Supervision</Table.Th>
                  <Table.Th>Appointment Status</Table.Th>
                  <Table.Th>Bank Details</Table.Th>
                </>
              ) : (
                <>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Start</Table.Th>
                  <Table.Th>End</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Appointment Status</Table.Th>
                  <Table.Th>Payment Status</Table.Th>
                  <Table.Th>Bank Details</Table.Th>
                </>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {showSummaryView ? (
              // Summary View - One row per person
              personSummaries.map((personSummary) => (
                <Table.Tr key={personSummary.personId}>
                  <Table.Td>
                    <Checkbox
                      checked={isPersonSelected(personSummary)}
                      indeterminate={isPersonIndeterminate(personSummary)}
                      onChange={() => togglePersonSelection(personSummary)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500}>
                      {`${personSummary.person.firstName} ${personSummary.person.lastName}`}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500}>
                      ${personSummary.totalAmount.toFixed(2)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {personSummary.gameCount > 0 && (
                        <Badge color="blue">
                          {personSummary.gameCount} Games
                        </Badge>
                      )}
                      {personSummary.supervisorCount > 0 && (
                        <Badge color="orange">
                          {personSummary.supervisorCount} Supervisor
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text>
                      {Array.from(personSummary.statusCounts.entries())
                        .map(([status, count]) => `${count} ${getStatusName(status)}`)
                        .join(', ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text c={personSummary.hasBankDetails ? 'dimmed' : 'red'}>
                      {personSummary.hasBankDetails ? '' : 'Missing bank details'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))
            ) : (
              // Detail View - One row per appointment
              filteredAppointments.map((appointment) => {
                const person = personMap.get(appointment.person);
                const hasBankDetails = !!(person?.hasBankDetails);

                return (
                  <Table.Tr key={appointment._id}>
                    <Table.Td>
                      <Checkbox
                        checked={selectedAppointments.has(appointment._id)}
                        onChange={() => toggleAppointment(appointment._id)}
                        disabled={!!appointment.payEventId}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>
                        {person ? `${person.firstName} ${person.lastName}` : 'Unknown Person'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>
                        {appointment.type}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text>
                        {DateTime.fromISO(appointment.start).toFormat('dd/MM/yyyy HH:mm')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text>
                        {DateTime.fromISO(appointment.finish).toFormat('dd/MM/yyyy HH:mm')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>
                        ${(appointment.payment?.total || 0).toFixed(2)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text>
                        {getStatusName(appointment.status ?? -1)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={appointment.payEventId ? 'green' : 'gray'}
                        variant="filled"
                       
                      >
                        {appointment.payEventId ? 'Paid' : 'Unpaid'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text c={hasBankDetails ? 'dimmed' : 'red'}>
                        {hasBankDetails ? '' : 'Missing bank details'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>

        {(showSummaryView ? personSummaries.length === 0 : filteredAppointments.length === 0) && (
          <Text c="dimmed" ta="center" py="xl">
            No appointments found with the selected filters
          </Text>
        )}
        {/* Error Display */}
        {(error || createError) && (
          <Alert color="red">
            <strong>Error:</strong> {error || createError}
          </Alert>
        )}

        {/* Pay Event Details */}
        <Stack gap="sm">
          <Text fw={500}>Pay Event Name</Text>
          <TextInput
            value={payEventName}
            onChange={(e) => onPayEventNameChange(e.target.value)}
            placeholder="Enter pay event name"
          />
        </Stack>

        <Stack gap="sm">
          <Text fw={500}>Description (Optional)</Text>
          <TextInput
            value={payEventDescription}
            onChange={(e) => onPayEventDescriptionChange(e.target.value)}
            placeholder="Enter description"
          />
        </Stack>

        {/* Summary and Actions */}
        <Group justify="space-between" mt="md">
          <Stack gap={0}>
            <Text>
              <strong>{selectedAppointments.size}</strong> appointments selected
            </Text>
            <Text fw={500}>
              Total: <strong>${selectedTotal.toFixed(2)}</strong>
            </Text>
          </Stack>

          <Group>
            <Button variant="outline" onClick={onClose} disabled={isCreatingPayEvent} color="red">
              Cancel
            </Button>
            <Button
              onClick={handleCreatePayEvent}
              disabled={selectedAppointments.size === 0 || !payEventName.trim()}
              loading={isCreatingPayEvent}
            >
              Create Pay Event
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal >
  );
};
